import { UserRoles } from '@/enums/enums';
import { useCustomEntities } from '@/features/custom-entity/custom-entity.queries';
import { getUserOptions } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { RoomDateColumn } from '@/components/datatable/columns/rooms/rooms-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';

export const Route = createFileRoute('/_app/(custom-entities)/custom-entities/')({
  component: CustomEntities,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function CustomEntities() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <CustomEntitiesTabHeader />
      <CustomEntitiesTable />
    </div>
  );
}

function CustomEntitiesTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['custom-entity', 'common']);

  const createRoom = () => {
    navigate({ to: '/custom-entities/builder', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createRoom} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {t('custom_entity', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
}

function CustomEntitiesTable() {
  const { t } = useTranslation(['room', 'common']);
  const navigate = useNavigate();
  const { data = [], isLoading } = useCustomEntities();

  const updateCustomEntity = (id: number) => {
    navigate({
      to: '/custom-entities/builder',
      search: { mode: 'update', entity: id },
    });
  };

  return (
    <DataTableSimple
      data={data}
      columns={['id', 'name', 'createdAt', 'updatedAt']}
      isLoading={isLoading}
      columnsLabels={{
        id: 'Serial No',
        name: toCapitalize(t('title', { ns: 'common' })),
        createdAt: t('created_at', { ns: 'common' }),
        updatedAt: t('updated_at', { ns: 'common' }),
      }}
      customRender={{
        createdAt: (row) => <RoomDateColumn date={row.getValue('createdAt')} />,
        updatedAt: (row) => <RoomDateColumn date={row.getValue('updatedAt')} />,
      }}
      extraColumns={[
        {
          id: 'actions',
          size: 50,
          cell: ({ row }) => (
            <DataTableRowActions
              row={row}
              menuItems={[
                {
                  label: toCapitalize(t('edit', { ns: 'common' })),
                  onClick: () => updateCustomEntity(row.getValue('id')),
                  isLoading: false,
                },
              ]}
            />
          ),
        },
      ]}
      className='flex h-full flex-col overflow-y-auto'
    />
  );
}
